import {
  AdminApi,
  AdminMail,
  AdminMailsListOrderingEnum,
  AdminMailsValuesRetrieveFieldEnum,
  AdminMailTypeEnum,
} from 'api';
import WithDataRecord from 'hoc/WithDataRecord';
import React, { FunctionComponent, useContext, useMemo } from 'react';
import Button from 'ui/atoms/button';
import Icon from 'ui/atoms/icon';
import Spacer from 'ui/atoms/spacer';
import DateAtom from 'ui/atoms/date';
import Translate from 'ui/atoms/translate';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import * as Styled from './styled';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

interface IssuerInvestmentsTableProps {
  forceDataLoad?: boolean;
  resendEmail: (id: string) => void;
  isEmailResent: (id: string) => boolean;
  defaultLimit?: number;
  investorId: string;
  skipFilterUrlSync?: boolean;
  hasPermission: boolean;
}

const EmailHistoryTableView: FunctionComponent<IssuerInvestmentsTableProps> = WithDataRecord<AdminApi, AdminMail>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, loading, hasPermission, resendEmail, isEmailResent, paginationProps, investorId }) => {
    const { activeLocale } = useContext(I18NContext);

    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const emailTypesFilterOptions = useMemo(() => {
      return Object.values(AdminMailTypeEnum).map((mailType) => ({
        id: mailType,
        name: translate(`emailType.${mailType}`),
      }));
    }, [translate]);

    return (
      <>
        <DataRecordTable
          ordering={ordering}
          onOrderBy={onOrderBy}
          data={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          searchComponent={
            <Search
              input="search"
              defaultField="search"
              label={translate('filters.labels.productOrSubject')}
              name="search"
            />
          }
          cells={[
            {
              title: <Translate name={`studioEmailHistoryTable.product`} />,
              render: ({ product }) => product,
              orderingFieldName: AdminMailsListOrderingEnum.PRODUCT_ASC,
              type: CellTypeEnum.text,
              filterName: 'productName',
              searchFieldName: AdminMailsValuesRetrieveFieldEnum.PRODUCT,
              getFilterData: (params: any) => adminApi.adminMailsValuesRetrieve({ ...params, investor: [investorId] }),
            },
            {
              title: <Translate name={`studioEmailHistoryTable.mailType`} />,
              render: ({ type }) => <Translate name={`emailType.${type}`} />,
              orderingFieldName: AdminMailsListOrderingEnum.TYPE_ASC,
              type: CellTypeEnum.text,
              filterName: 'type',
              searchFieldName: AdminMailsValuesRetrieveFieldEnum.TYPE,
              getFilterData: () => {
                return emailTypesFilterOptions;
              },
            },
            {
              title: <Translate name={`studioEmailHistoryTable.mailSubject`} />,
              render: ({ subject }) => subject,
              orderingFieldName: AdminMailsListOrderingEnum.SUBJECT_ASC,
              type: CellTypeEnum.text,
              filterName: 'subject',
              searchFieldName: AdminMailsValuesRetrieveFieldEnum.SUBJECT,
              getFilterData: (params: any) => adminApi.adminMailsValuesRetrieve({ ...params, investor: [investorId] }),
            },
            {
              title: <Translate name={`studioEmailHistoryTable.deliveredDate`} args={[<>&shy;</>]} />,
              render: ({ deliveredAt }) => deliveredAt && <DateAtom showTime>{new Date(deliveredAt)}</DateAtom>,
              orderingFieldName: AdminMailsListOrderingEnum.SEND_AT_ASC,
              type: CellTypeEnum.date,
              filterName: 'deliveredAf',
            },
            {
              title: null,
              alignContent: 'right',
              width: activeLocale === 'de' ? 148 : 106,
              render: ({ id }) => {
                const emailResent = isEmailResent(id);
                return (
                  <Button
                    type="submit"
                    size="xsmall"
                    variant="secondary"
                    disabled={emailResent}
                    onClick={() => resendEmail(id)}
                  >
                    {emailResent ? (
                      <Styled.EmailResentMessage>
                        <Translate name="studioEmailHistoryTable.resent" />
                        <Spacer x={2} />
                        <Icon color="link" name="check" />
                      </Styled.EmailResentMessage>
                    ) : (
                      <Translate name="studioEmailHistoryTable.resend" />
                    )}
                  </Button>
                );
              },
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminMailsList({
      investor: [props.investorId],
      offset,
      limit,
      ordering: ordering as AdminMailsListOrderingEnum,
      ...props,
    });
  },
);

export default EmailHistoryTableView;
